import axios from 'axios';
import { apiRoute } from '../constants/api-constants';
import {
  HandoffCandidatesResponse,
  HandoffCreatedResponse,
  LatestHandoffResponse,
  HandoffListResponse,
  HandoffListRole,
  HandoffLogResponse,
  HandoffReadResponse,
} from '../interfaces/handoff-interfaces';
import type { ActivityGroup } from './activity-client';

/* Typed wrapper over the shift HANDOVER (lobbero_be/src/CRUD/shift-handoff.js,
 * mounted at app.use('/api', shiftHandoffRoutes) — index.js:227 — so every path
 * here is /api/shifts/handoff*).
 *
 * Default axios instance, like shifts-client and iq-client, so every call
 * inherits axios-setup.ts: withCredentials for the httpOnly session cookies, the
 * X-CSRF-Token double submit, and the single-flight 401 → refresh → retry. There
 * is no auth code in this file and there must not be.
 *
 * ═══════════ THE TIMEOUTS ARE ASYMMETRIC, AND THAT IS THE POINT ═══════════
 * The reads are four short SQL statements against a FROZEN snapshot — no window
 * fetch, no citation resolution, no model call — so they get a short timeout and
 * must not borrow iq-client's 75s. A concierge staring at a dead spinner for a
 * minute because the API is down is worse than a fast failure they can retry.
 *
 * `citations` (WP-10 step 2) rides on the SAME frozen object every route here
 * returns and is resolved server-side inside the request — see `Handoff` in
 * interfaces/handoff-interfaces.ts. Nothing in this file touches it: the axios
 * generics hand the body through untouched, which is exactly why an older server
 * that omits the key costs the FE nothing. Do not "normalise" it here, and above
 * all do not resolve references client-side — that would turn a snapshot the
 * server froze into a live view of today's log.
 *
 * POST /shifts/handoff IS THE OPPOSITE. It closes the shift, writes the record,
 * and THEN calls the model inline (phase B), so its worst case is the whole IQ
 * budget: the semaphore wait, two attempts, and the vendor timeout. It gets the
 * same 75s iq-client derives for the same reason — and an abort here does NOT
 * undo anything, because by the time the vendor call starts the shift is already
 * closed and the handover already exists on disk. Giving up early loses the
 * upgraded WORDING, never the handover.
 */
const READ_TIMEOUT = 15000;
const HANDOFF_TIMEOUT = 75000;

interface HandoffErrorBody {
  message?: string;
  code?: string;
}

/** The 404 from POST /shifts/handoff. Its own class for the same reason
 *  shifts-client has one: it is a benign disagreement — the client thought a
 *  shift was open at this building and it was not — and reads as "you are
 *  already clocked out", never as a failure. */
export class NoOpenShiftForHandoffError extends Error {
  constructor(message: string) {
    super(message);
    this.name = 'NoOpenShiftForHandoffError';
  }
}

/** The recipient was refused. ONE message covers every failing reason (wrong
 *  organisation, not staff, inactive, not scoped to this building, does not
 *  exist), deliberately: the endpoint must not become an oracle for "is <id> a
 *  user here". So the UI must not try to explain WHICH — it re-reads the picker
 *  and shows the server's own sentence. */
export class RecipientNotEligibleError extends Error {
  constructor(message: string) {
    super(message);
    this.name = 'RecipientNotEligibleError';
  }
}

const asHandoffError = (error: unknown, fallback: string): unknown => {
  if (!axios.isAxiosError(error)) return error;
  const status = error.response?.status;
  const data = error.response?.data as HandoffErrorBody | undefined;

  if (status === 404 && data?.code === 'NO_OPEN_SHIFT') {
    return new NoOpenShiftForHandoffError(
      data.message || 'You have no open shift at this building.'
    );
  }
  if (status === 400 && data?.code === 'RECIPIENT_NOT_ELIGIBLE') {
    return new RecipientNotEligibleError(
      data.message || 'That person cannot receive this handover.'
    );
  }
  // The server's own sentence beats anything written here — "You cannot hand a
  // shift over to yourself" is more useful than any generic fallback.
  if (data?.message) return new Error(data.message);

  return new Error(fallback);
};

/** The people picker. Gated on `shift:clock`, NOT on `user:read` — the concierge
 *  is the role that actually works a desk shift and does not hold user:read.
 *
 *  It returns the staff of ONE building, with no email, no phone and no scope
 *  detail. Do not filter the result further: the scope rule is already applied
 *  server-side and every row that arrives is eligible. */
export const getHandoffCandidates = (
  buildingId: number
): Promise<HandoffCandidatesResponse> =>
  axios
    .get<HandoffCandidatesResponse>(`${apiRoute}/shifts/handoff/candidates`, {
      params: { building_id: buildingId },
      timeout: READ_TIMEOUT,
    })
    .then(r => r.data)
    .catch(e => {
      throw asHandoffError(e, 'Could not read who is available to take over.');
    });

/** END THE SHIFT AND PUBLISH THE HANDOVER. One call does both.
 *
 *  It is not "clock out, then send a summary": the close and the record are one
 *  transaction server-side, so a failure leaves the shift OPEN rather than
 *  clocking somebody out into a handover that was never made.
 *
 *  `recipientUserId` IS OPTIONAL AND NO SCREEN PASSES IT (backend migration
 *  076). Called with one argument the handover is addressed to the DESK: owners
 *  and property managers covering the building are notified immediately, and the
 *  next person to clock in claims it. That is the whole point of the change —
 *  the previous shape required a colleague to be pickable at that exact minute,
 *  and on a single-concierge desk there never was one, so the only way to leave
 *  a record was unreachable. The second argument is kept because the endpoint
 *  still honours it. */
export const endShiftWithHandoff = (
  buildingId: number,
  recipientUserId?: string,
  opts?: { signal?: AbortSignal }
): Promise<HandoffCreatedResponse> =>
  axios
    .post<HandoffCreatedResponse>(
      `${apiRoute}/shifts/handoff`,
      {
        building_id: buildingId,
        // OMITTED, not sent as null: the server reads
        // `String(req.body.recipient_user_id || '')`, so either spelling works,
        // but a body that does not mention a recipient is the honest wire
        // representation of "there is no recipient".
        ...(recipientUserId ? { recipient_user_id: recipientUserId } : {}),
      },
      { timeout: HANDOFF_TIMEOUT, signal: opts?.signal }
    )
    .then(r => r.data)
    .catch(e => {
      throw asHandoffError(e, 'Could not complete the handover.');
    });

/** What the incoming person's IQ already has — D-25 point 4, "they do not have
 *  to ask". Cheap by construction: no window fetch, no model call.
 *
 *  `buildingId` narrows it to ONE DESK. Anyone who covers more than one
 *  building — every property manager, and a floating concierge — would
 *  otherwise be handed the newest handover anywhere in their scope, which is
 *  usually not the desk on their screen. It is a filter and never an
 *  authorisation: RLS has already restricted the rows to buildings this caller
 *  can see, so naming a building can only ever return fewer of them. */
export const getLatestHandoff = (
  buildingId?: number | null
): Promise<LatestHandoffResponse> =>
  axios
    .get<LatestHandoffResponse>(`${apiRoute}/shifts/handoff/latest`, {
      timeout: READ_TIMEOUT,
      params:
        buildingId != null && Number.isInteger(buildingId)
          ? { building_id: buildingId }
          : undefined,
    })
    .then(r => r.data)
    .catch(e => {
      throw asHandoffError(e, 'Could not read your latest handover.');
    });

/** PAST handovers — D-25 point 5.
 *
 *  `received` and `sent` are the caller's own inbox and outbox and need only
 *  `shift:clock`. `building` is the whole desk's history, needs `shift:manage`,
 *  and REQUIRES `buildingId`: it is what makes a handover nobody opened
 *  discoverable rather than merely undeleted. */
export const listHandoffs = (params: {
  role: HandoffListRole;
  buildingId?: number;
  limit?: number;
  unreadOnly?: boolean;
}): Promise<HandoffListResponse> =>
  axios
    .get<HandoffListResponse>(`${apiRoute}/shifts/handoff`, {
      params: {
        role: params.role,
        ...(params.buildingId != null
          ? { building_id: params.buildingId }
          : {}),
        ...(params.limit != null ? { limit: params.limit } : {}),
        ...(params.unreadOnly ? { unread: 'true' } : {}),
      },
      timeout: READ_TIMEOUT,
    })
    .then(r => r.data)
    .catch(e => {
      throw asHandoffError(e, 'Could not read your past handovers.');
    });

/** THE SHIFT, ENTRY BY ENTRY — every event logged in the handover's frozen
 *  window, oldest first, read LIVE (see HandoffLogResponse). Paged: a very busy
 *  desk can run to hundreds of rows, and the reader asks for the next page.
 *
 *  `group` narrows to one tab; the response's counts stay whole-list. */
export const getHandoffLog = (
  id: string,
  params: { limit?: number; offset?: number; group?: ActivityGroup[] } = {},
  opts: { signal?: AbortSignal } = {}
): Promise<HandoffLogResponse> =>
  axios
    .get<HandoffLogResponse>(
      `${apiRoute}/shifts/handoff/${encodeURIComponent(id)}/log`,
      {
        params: {
          ...(params.limit != null ? { limit: params.limit } : {}),
          ...(params.offset != null ? { offset: params.offset } : {}),
          ...(params.group && params.group.length
            ? { group: params.group.join(',') }
            : {}),
        },
        timeout: READ_TIMEOUT,
        signal: opts.signal,
      }
    )
    .then(r => r.data)
    .catch(e => {
      throw asHandoffError(e, 'Could not read the log for this handover.');
    });

/** Mark it read. ONLY the recipient may — a 403 here is correct behaviour for a
 *  manager browsing the desk's history, not a bug, so callers treat it as a
 *  no-op rather than an error worth shouting about.
 *
 *  IDEMPOTENT: the FIRST open is the timestamp that stands. */
export const markHandoffRead = (id: string): Promise<HandoffReadResponse> =>
  axios
    .post<HandoffReadResponse>(`${apiRoute}/shifts/handoff/${id}/read`, null, {
      timeout: READ_TIMEOUT,
    })
    .then(r => r.data)
    .catch(e => {
      throw asHandoffError(e, 'Could not mark the handover read.');
    });
